import { ArrowLeft, ArrowUpRight, FileCheck2, LockKeyhole, Mail, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Footer } from "@/components/Footer";
import { Navbar } from "@/components/Navbar";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { PRIVACY_POLICY } from "@/config/privacyPolicy";
import { useLang } from "@/i18n/LangContext";
import { useSettings } from "@/context/AppSettings";

const POLICY_URL = "https://melnikbrokers.ae/privacy-policy";

export default function PrivacyPolicyPage() {
  const { lang } = useLang();
  const settings = useSettings();
  const isRu = lang === "ru";
  const content = isRu ? PRIVACY_POLICY.ru : PRIVACY_POLICY.en;
  const contactEmail = settings.email || PRIVACY_POLICY.contactEmail;
  const brand = settings.legal_name || settings.company_name || "MELNIK Commercial Brokers";

  return (
    <div id="top" className="min-h-screen bg-[#0A0B0E] text-slate-100">
      <div className="grain-overlay" />
      <Navbar />
      <main>
        <section className="relative overflow-hidden border-b border-white/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_8%,rgba(212,175,55,0.16),transparent_29%),radial-gradient(circle_at_8%_90%,rgba(10,64,108,0.18),transparent_38%)]" />
          <div className="relative mx-auto max-w-7xl px-5 pb-16 pt-36 sm:px-8 sm:pb-24 sm:pt-44">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-[#F5E5AB]">
              <ArrowLeft className="h-4 w-4" /> {content.homeAction}
            </Link>
            <p className="mt-10 text-xs font-semibold uppercase tracking-[0.28em] text-[#D4AF37]">{content.eyebrow}</p>
            <div className="mt-5 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
              <div>
                <h1 className="max-w-4xl font-serif text-4xl leading-[1.04] text-[#F5F0E4] sm:text-6xl">{content.title}</h1>
                <p className="mt-7 max-w-3xl text-lg leading-relaxed text-slate-300 sm:text-xl">{content.lead}</p>
              </div>
              <aside className="rounded-3xl border border-[#D4AF37]/30 bg-[#12141C]/80 p-6 shadow-2xl backdrop-blur-sm sm:p-7">
                <div className="flex items-center gap-3 text-[#D4AF37]">
                  <FileCheck2 className="h-5 w-5" />
                  <span className="text-xs font-semibold uppercase tracking-[0.18em]">Meta Lead Ads</span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">{content.metaNotice}</p>
                <a href={POLICY_URL} className="mt-5 inline-flex break-all text-sm font-medium text-[#F5E5AB] underline decoration-[#D4AF37]/55 underline-offset-4 transition-colors hover:text-white">
                  {POLICY_URL}
                </a>
              </aside>
            </div>
          </div>
        </section>

        <article className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <section className="grid gap-5 border-y border-white/10 py-7 sm:grid-cols-[1fr_auto] sm:items-center">
            <div className="flex items-start gap-4">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" />
              <p className="max-w-3xl text-sm leading-relaxed text-slate-400">
                {isRu
                  ? `${brand}. ${content.updatedLabel}: ${PRIVACY_POLICY.updated}.`
                  : `${brand}. ${content.updatedLabel}: ${PRIVACY_POLICY.updated}.`}
              </p>
            </div>
            <a href={`mailto:${contactEmail}`} className="inline-flex shrink-0 items-center gap-2 text-sm font-medium text-[#F5E5AB] transition-colors hover:text-white">
              <Mail className="h-4 w-4" /> {content.contactAction} <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </section>

          <div className="mx-auto mt-16 max-w-4xl space-y-14 sm:mt-20">
            {content.sections.map((section, index) => (
              <section key={section.title} className="scroll-mt-28" id={`privacy-section-${index + 1}`}>
                <div className="flex gap-4">
                  <LockKeyhole className="mt-1 h-5 w-5 shrink-0 text-[#D4AF37]" />
                  <div>
                    <h2 className="font-serif text-2xl leading-tight text-[#F5F0E4] sm:text-3xl">{section.title}</h2>
                    <div className="mt-5 space-y-4 text-[15px] leading-8 text-slate-300 sm:text-base">
                      {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>

          <section className="mx-auto mt-20 max-w-4xl rounded-3xl border border-[#D4AF37]/35 bg-[#12141C] p-7 sm:p-10">
            <div className="flex gap-4">
              <LockKeyhole className="mt-1 h-5 w-5 shrink-0 text-[#D4AF37]" />
              <div>
                <h2 className="font-serif text-3xl text-[#F5F0E4]">{content.deletionTitle}</h2>
                <p className="mt-5 max-w-3xl leading-relaxed text-slate-300">{content.deletionBody}</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <a href={`mailto:${contactEmail}?subject=${encodeURIComponent("Personal Data Deletion Request")}`} className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-3 text-sm font-semibold text-[#0A0B0E] transition-transform hover:scale-[1.02] active:scale-95">
                    <Mail className="h-4 w-4" /> {content.deletionEmailLabel}
                  </a>
                  <Link to="/#contact" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-[#D4AF37]/60 hover:text-[#F5E5AB]">
                    {isRu ? "Перейти к форме сайта" : "Open website contact form"} <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </section>

          <section className="mx-auto mt-20 max-w-4xl rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-7 sm:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#D4AF37]">{isRu ? "Справочная информация" : "Reference information"}</p>
            <h2 className="mt-4 font-serif text-3xl text-[#F5F0E4]">{isRu ? "Защита персональных данных в ОАЭ" : "Personal data protection in the UAE"}</h2>
            <p className="mt-4 max-w-3xl leading-relaxed text-slate-300">
              {isRu
                ? "Для общей справки о законодательстве ОАЭ о защите персональных данных используйте официальный ресурс UAE Government. Для конкретной ситуации может потребоваться независимая юридическая консультация."
                : "For general information about UAE personal-data protection law, consult the UAE Government resource. Independent legal advice may be appropriate for a specific situation."}
            </p>
            <a href={PRIVACY_POLICY.uaePdplUrl} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-slate-200 transition-colors hover:border-[#D4AF37]/60 hover:text-[#F5E5AB]">
              {isRu ? "Открыть UAE Government resource" : "Open UAE Government resource"} <ArrowUpRight className="h-4 w-4" />
            </a>
          </section>
        </article>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
